import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { api, clearApiToken, hasApiToken, saveApiToken } from '../services/api'
import type { Restaurant, Session, User } from '../types'

const restaurantKey = 'foodairway_restaurant_web_restaurant'
const branchKey = 'foodairway_restaurant_web_branch'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const restaurants = ref<Restaurant[]>([])
  const selectedRestaurantId = ref<number | null>(Number(localStorage.getItem(restaurantKey)) || null)
  const selectedBranchId = ref<number | null>(Number(localStorage.getItem(branchKey)) || null)
  const booting = ref(hasApiToken())

  const authenticated = computed(() => Boolean(user.value && hasApiToken()))
  const selectedRestaurant = computed(() => restaurants.value.find((row) => row.id === selectedRestaurantId.value) || null)
  const selectedBranch = computed(() => selectedRestaurant.value?.branches.find((row) => row.id === selectedBranchId.value) || null)

  function useSession(session: Session) {
    saveApiToken(session.token)
    user.value = session.user
    restaurants.value = session.restaurants || []
    normalizeContext()
  }

  function normalizeContext() {
    if (!restaurants.value.some((row) => row.id === selectedRestaurantId.value)) {
      selectedRestaurantId.value = restaurants.value[0]?.id || null
    }
    const restaurant = restaurants.value.find((row) => row.id === selectedRestaurantId.value)
    if (!restaurant?.branches.some((row) => row.id === selectedBranchId.value)) {
      selectedBranchId.value = restaurant?.branches[0]?.id || null
    }
    persistContext()
  }

  function persistContext() {
    selectedRestaurantId.value ? localStorage.setItem(restaurantKey, String(selectedRestaurantId.value)) : localStorage.removeItem(restaurantKey)
    selectedBranchId.value ? localStorage.setItem(branchKey, String(selectedBranchId.value)) : localStorage.removeItem(branchKey)
  }

  function selectRestaurant(id: number) {
    selectedRestaurantId.value = id
    const restaurant = restaurants.value.find((row) => row.id === id)
    selectedBranchId.value = restaurant?.branches[0]?.id || null
    persistContext()
  }

  function selectBranch(id: number) {
    selectedBranchId.value = id
    persistContext()
  }

  async function restore() {
    if (!hasApiToken()) {
      booting.value = false
      return false
    }
    try {
      const { data } = await api.get('/restaurant/me')
      user.value = data.data.user
      restaurants.value = data.data.restaurants || []
      normalizeContext()
      return true
    } catch {
      clear()
      return false
    } finally {
      booting.value = false
    }
  }

  async function logout() {
    try {
      await api.post('/restaurant/logout')
    } finally {
      clear()
    }
  }

  function clear() {
    clearApiToken()
    user.value = null
    restaurants.value = []
  }

  return {
    user,
    restaurants,
    selectedRestaurantId,
    selectedBranchId,
    selectedRestaurant,
    selectedBranch,
    authenticated,
    booting,
    useSession,
    restore,
    logout,
    clear,
    selectRestaurant,
    selectBranch,
  }
})
